/**
 * Camlin Companion — desktop panel layout helpers (round 5, 6 Aug 2026).
 *
 * Two Keith items live here:
 *   - half-mode divider ratio: the visitor can drag the boundary between
 *     the website and the avatar panel; the chosen ratio persists across
 *     visits (same storage contract as consent.ts / voiceHint.ts).
 *   - host-page push: when the tall or half panel is active the page must
 *     be fully viewable in its own region, not overlaid — classic
 *     side-drawer behaviour via a margin on the host <body>.
 */

import type { CompanionConsentStorage } from './consent';

export const COMPANION_HALF_RATIO_STORAGE_KEY = 'camlin-companion-half-ratio-v1';

/** Panel fraction of the viewport width in half mode. */
export const HALF_RATIO_DEFAULT = 0.5;
export const HALF_RATIO_MIN = 0.25;
export const HALF_RATIO_MAX = 0.75;

/**
 * Split-view page-region floor (v8 item 5). The pushed website region can
 * never be narrower than this: below ~480px a desktop layout squeezed by
 * the body-margin push (which does NOT re-fire viewport media queries —
 * they key on window width, and only an iframe would change that) stops
 * being usable at all. The half-panel width is expressed as
 * min(ratio·100vw, 100vw − this), so the guarantee holds on every window
 * size without touching the persisted ratio.
 */
export const SPLIT_PAGE_MIN_WIDTH_PX = 480;

/** CSS width expression for the half-mode panel honouring the page floor. */
export function buildHalfPanelWidth(ratio: number): string {
  return `min(${(clampHalfRatio(ratio) * 100).toFixed(2)}vw, calc(100vw - ${SPLIT_PAGE_MIN_WIDTH_PX}px))`;
}

export function clampHalfRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return HALF_RATIO_DEFAULT;
  return Math.min(HALF_RATIO_MAX, Math.max(HALF_RATIO_MIN, ratio));
}

export function readCompanionHalfRatio(storage: CompanionConsentStorage): number {
  const raw = storage.getItem(COMPANION_HALF_RATIO_STORAGE_KEY);
  if (!raw) return HALF_RATIO_DEFAULT;
  return clampHalfRatio(Number.parseFloat(raw));
}

export function writeCompanionHalfRatio(storage: CompanionConsentStorage, ratio: number): void {
  storage.setItem(COMPANION_HALF_RATIO_STORAGE_KEY, String(clampHalfRatio(ratio)));
}

/**
 * Push the host page out from under the panel by putting a right margin on
 * <body> (the classic side-drawer embed move — flow content reflows into
 * the remaining region; the panel owns the rest). Returns a restore
 * function that puts the body's inline styles back exactly as found, so
 * host pages that set their own body margins are never clobbered.
 */
export function pushHostPage(width: string, mode?: 'half' | 'tall'): () => void {
  if (typeof document === 'undefined') {
    return () => { /* SSR no-op */ };
  }
  const body = document.body;
  const prevMargin = body.style.marginRight;
  const prevTransition = body.style.transition;
  body.style.transition = 'margin-right 0.25s ease';
  body.style.marginRight = width;
  // Split-view host hook (v8 item 5): the margin push cannot re-fire the
  // page's viewport media queries (they key on window width), so give the
  // host CSS something IT can key on instead:
  //   html[data-camlin-companion-split] .hero { … }        ← attribute
  //   width: var(--camlin-companion-page-width, 100vw);    ← live variable
  // Camlin-owned sites pair this with container queries on their key
  // layouts (see the split-view spec shipped with Companion v8).
  const root = document.documentElement;
  if (mode) {
    root.setAttribute('data-camlin-companion-split', mode);
    root.style.setProperty('--camlin-companion-page-width', `calc(100vw - ${width})`);
  }
  return () => {
    body.style.marginRight = prevMargin;
    body.style.transition = prevTransition;
    if (mode) {
      root.removeAttribute('data-camlin-companion-split');
      root.style.removeProperty('--camlin-companion-page-width');
    }
  };
}
